/**
 * Forme as a pure measurer.
 *
 * Forme is page-native: given a document it paginates and draws. But given a
 * page tall enough that nothing can overflow, it becomes a measurement engine —
 * it returns the laid-out tree with a box for every node, a `TextLine` per line
 * of wrapped text and a `TableRow` per row. That is exactly the geometry our own
 * paginator consumes, so BroadPaper keeps every pagination decision (keep
 * together, keep with next, orphans and widows, repeated table headers, page
 * breaks, splitting multi-column rows) while no browser is involved anywhere.
 *
 * Correlating engine output back to BroadPaper nodes is done structurally: the
 * translator records an annotation tree in lockstep with the nodes it emits, and
 * the layout tree comes back with the same shape (leaf `Text` nodes merely gain
 * `TextLine` children, which appends rather than reorders).
 */
import type { GeometryMap, NodeGeometry, ResolvedNode, SectionGeometry, TableRowGeometry } from "@broadpaper/core";
import type { FormeNode } from "./vnode.js";

/** Mirrors an emitted node, carrying what we need to map geometry back. */
export interface Annotation {
  /** BroadPaper node key, when this node is a block wrapper. */
  key?: string;
  /** This node's subtree holds wrapped text whose line boxes we want. */
  text?: boolean;
  /** Row roles in emission order, for table nodes. */
  rowKinds?: Array<TableRowGeometry["kind"]>;
  children: Annotation[];
}

/** A layout element as returned by Forme. */
export interface LayoutElement {
  x: number;
  y: number;
  width: number;
  height: number;
  nodeType: string;
  textContent?: string | null;
  children?: LayoutElement[];
}

export interface LayoutPage {
  width: number;
  height: number;
  elements: LayoutElement[];
}

/** Height of the measurement page. Tall enough that nothing paginates. */
export const UNBOUNDED_PAGE_HEIGHT = 200_000;

function descendants(el: LayoutElement, nodeType: string, out: LayoutElement[] = []): LayoutElement[] {
  for (const c of el.children ?? []) {
    if (c.nodeType === nodeType) out.push(c);
    descendants(c, nodeType, out);
  }
  return out;
}

/**
 * Walks the annotation tree and the layout tree together, recording a geometry
 * entry for every annotated node.
 */
function collect(ann: Annotation, el: LayoutElement, origin: { x: number; y: number }, out: Record<string, NodeGeometry>): void {
  if (ann.key) {
    const g: NodeGeometry = {
      top: round(el.y - origin.y),
      height: round(el.height),
      // Both coordinates are relative to the section, which is what the
      // paginator hands back as a repeated header’s offset inside the page
      // content box. Leaving x absolute pushed every repeated table header one
      // page margin to the right and its last column off the page.
      left: round(el.x - origin.x),
      width: round(el.width)
    };
    if (ann.text) {
      const lines = descendants(el, "TextLine");
      if (lines.length) {
        // The paginator splits at the boundary *between* lines, so record the
        // midpoint of each gap; the last entry is the node's own bottom.
        const sorted = [...lines].sort((a, b) => a.y - b.y);
        const bounds: number[] = [];
        for (let i = 0; i < sorted.length - 1; i++) {
          const bottom = sorted[i]!.y + sorted[i]!.height;
          bounds.push(round((bottom + sorted[i + 1]!.y) / 2 - origin.y));
        }
        bounds.push(round(el.y + el.height - origin.y));
        g.lines = bounds;
      }
    }
    if (ann.rowKinds) {
      const rows = descendants(el, "TableRow").sort((a, b) => a.y - b.y);
      g.rows = rows.map((r, i) => ({ top: round(r.y - origin.y), height: round(r.height), kind: ann.rowKinds![i] ?? "body" }));
    }
    out[ann.key] = g;
  }
  const kids = el.children ?? [];
  for (let i = 0; i < ann.children.length; i++) {
    const childEl = kids[i];
    if (!childEl) continue;
    collect(ann.children[i]!, childEl, origin, out);
  }
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface MeasuredSection {
  section: ResolvedNode;
  node: FormeNode;
  ann: Annotation;
}

/**
 * Builds the geometry map our paginator expects from Forme's layout of an
 * unbounded page. `pageElements` are the top-level elements of that page, in the
 * same order as `sections`.
 */
export function geometryFromLayout(sections: MeasuredSection[], pageElements: LayoutElement[], width: number): GeometryMap {
  const out: GeometryMap = {};
  for (let i = 0; i < sections.length; i++) {
    const el = pageElements[i];
    const { section, ann } = sections[i]!;
    if (!el) {
      out[section.key] = { height: 0, width, nodes: {} };
      continue;
    }
    const nodes: Record<string, NodeGeometry> = {};
    collect(ann, el, { x: el.x, y: el.y }, nodes);
    const geom: SectionGeometry = { height: round(el.height), width, nodes };
    // The section itself is the origin, so it always starts at zero.
    nodes[section.key] = { top: 0, height: geom.height, left: 0, width: round(el.width) };
    out[section.key] = geom;
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Measurer                                                            */
/* ------------------------------------------------------------------ */

import { renderNode, type BlockRegistry, type DocumentStyles, type FormatContext, type RenderMode, type RenderTreeOptions, type Theme } from "@broadpaper/core";
import { translate, type TranslateContext } from "./vnode.js";
import { pxToPt } from "./style.js";

export interface FormeRenderer {
  (doc: Record<string, unknown>): Promise<{ pdf: Uint8Array; layout?: unknown; warnings?: string[] }>;
}

export interface FormeMeasurerOptions {
  registry: BlockRegistry;
  theme: Theme;
  styles: DocumentStyles;
  format: FormatContext;
  mode?: RenderMode;
  /** Page width in CSS pixels, used for the measurement page. */
  pageWidth: number;
  margins: { top: number; right: number; bottom: number; left: number };
  fonts?: unknown[];
  /** Injected so the caller picks the Forme entry point (node, browser, worker). */
  render: FormeRenderer;
}

function scale(el: LayoutElement, factor: number): LayoutElement {
  return {
    ...el,
    x: el.x * factor,
    y: el.y * factor,
    width: el.width * factor,
    height: el.height * factor,
    children: (el.children ?? []).map((c) => scale(c, factor))
  };
}

/**
 * Measures resolved sections with Forme, returning geometry in CSS pixels — the
 * same shape `DomMeasurer` produces, so the paginator and the editor cannot tell
 * which one measured.
 */
export class FormeMeasurer {
  readonly warnings = new Set<string>();

  constructor(private readonly opts: FormeMeasurerOptions) {}

  private renderOptions(): RenderTreeOptions {
    return {
      registry: this.opts.registry,
      theme: this.opts.theme,
      styles: this.opts.styles,
      mode: this.opts.mode ?? "preview",
      page: { number: 1, total: 1 },
      format: this.opts.format,
      width: this.contentWidth
    };
  }

  get contentWidth(): number {
    return Math.max(0, this.opts.pageWidth - this.opts.margins.left - this.opts.margins.right);
  }

  async measure(sections: ResolvedNode[], width = this.contentWidth): Promise<GeometryMap> {
    if (!sections.length) return {};
    const ctx: TranslateContext = { warnings: this.warnings, design: (this.opts.mode ?? "preview") === "design", suppressPageBreaks: true };
    const renderOpts = { ...this.renderOptions(), width };
    const measured: MeasuredSection[] = [];
    for (const section of sections) {
      const v = renderNode(section, renderOpts, width);
      if (!v) continue;
      const t = translate(v, ctx);
      if (t) measured.push({ section, node: t.node, ann: t.ann });
    }
    if (!measured.length) return {};

    const m = this.opts.margins;
    const doc: Record<string, unknown> = {
      children: [
        {
          kind: {
            type: "Page",
            config: {
              // Tall enough that nothing can paginate: this is a measuring page.
              size: { Custom: { width: pxToPt(this.opts.pageWidth), height: UNBOUNDED_PAGE_HEIGHT } },
              margin: { top: pxToPt(m.top), right: pxToPt(m.right), bottom: pxToPt(m.bottom), left: pxToPt(m.left) },
              wrap: true
            }
          },
          style: {},
          children: measured.map((x) => x.node)
        }
      ],
      metadata: {},
      defaultPage: { size: "A4", margin: { top: 54, right: 54, bottom: 54, left: 54 }, wrap: true },
      defaultStyle: {}
    };
    // A copy of each entry, never the caller's own. The engine resolves fonts in
    // place — bytes become a bare base64 string, which the *next* render then
    // reads as a path or fetches as a URL — so handing it this array made the first
    // measurement work and every later one throw. `fontEntries` in api.ts copies
    // for the same reason.
    if (this.opts.fonts?.length) doc.fonts = this.opts.fonts.map((f) => ({ ...(f as Record<string, unknown>) }));

    const result = await this.opts.render(doc);
    const pages = ((result.layout as { pages?: LayoutPage[] } | null)?.pages ?? []) as LayoutPage[];
    // Points back to CSS pixels; BroadPaper works in pixels throughout.
    const toPx = 96 / 72;
    const elements = (pages[0]?.elements ?? []).map((e) => scale(e, toPx));
    for (const w of result.warnings ?? []) this.warnings.add(w);
    return geometryFromLayout(measured, elements, width);
  }
}
